import { FunctionComponent } from 'react';
import { Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { SeasonFormData } from '../../schemas';
import { TeamLite } from './types';
import { GroupTeamsCard } from './GroupTeamsCard';

const PREFIX = 'pages.application_season.components.season_step3_teams.groups';

type GroupDistributionSectionProps = {
  /** Pool de times selecionados (já adicionados em Times participantes). */
  selectedTeams: TeamLite[];
  /** Cache de metadados por uuid (resolve nome/logo a partir de team_uuid). */
  metadataByUuid: Record<string, TeamLite>;
  isReadOnly?: boolean;
};

export const GroupDistributionSection: FunctionComponent<GroupDistributionSectionProps> = ({
  selectedTeams,
  metadataByUuid,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<SeasonFormData>();
  const { fields } = useFieldArray({ control, name: 'group_stages' });
  const groupStages = watch('group_stages') ?? [];

  // Times já alocados em QUALQUER grupo — não aparecem no picker dos demais.
  const allocatedTeamUuids = new Set(
    groupStages.flatMap(group => group.team_uuids ?? []),
  );

  const availableTeams = selectedTeams.filter(
    team => !allocatedTeamUuids.has(team.uuid),
  );

  const addToGroup = (groupIndex: number, teamUuid: string) => {
    const current = watch(`group_stages.${groupIndex}.team_uuids`) ?? [];
    if (current.includes(teamUuid)) return;
    setValue(`group_stages.${groupIndex}.team_uuids`, [...current, teamUuid], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const removeFromGroup = (groupIndex: number, teamUuid: string) => {
    const current = watch(`group_stages.${groupIndex}.team_uuids`) ?? [];
    setValue(
      `group_stages.${groupIndex}.team_uuids`,
      current.filter(uuid => uuid !== teamUuid),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <Flex direction="column" gap={3}>
      <Flex align="center" gap={2} color="gray.700">
        <ChakraFeatherIcon name="grid" boxSize="16px" color="brand.500" />
        <Text fontWeight="semibold">{t(`${PREFIX}.section_title`)}</Text>
      </Flex>
      <Text fontSize="13px" color="gray.500">
        {t(`${PREFIX}.section_help`)}
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
        {fields.map((field, index) => {
          const teamUuids = groupStages[index]?.team_uuids ?? [];
          const teams: TeamLite[] = teamUuids
            .map(uuid => metadataByUuid[uuid])
            .filter((team): team is TeamLite => !!team);

          return (
            <GroupTeamsCard
              key={field.id}
              groupName={
                groupStages[index]?.group_name?.trim() ||
                t(`${PREFIX}.placeholder_group_name`, { index: index + 1 })
              }
              teams={teams}
              availableTeams={availableTeams}
              isReadOnly={isReadOnly}
              onAdd={teamUuid => addToGroup(index, teamUuid)}
              onRemove={teamUuid => removeFromGroup(index, teamUuid)}
            />
          );
        })}
      </SimpleGrid>
    </Flex>
  );
};
